import { Dialog } from "../components-v2/Dialog";
import { TMessage } from "../i18n/tt";
import { ButtonHTMLAttributes, FC, forwardRef, useMemo, useState } from "react";
import { MTextInput } from "../components-v2/TextInput";
import { ApiService } from "../api";
import { failure, isOk, isPending, pending, Portfolio, Result } from "../common/common";
import {
  AcceptedTermsPreparedState,
  portfolioAllCompanies,
  portfolioMyInvestments,
  PortfolioSettingsActions,
  PortfolioSettingsState,
  portfolioWithAdminRole,
  portfolioWithBoardRole,
} from "./portfolioDomain";
import { observer } from "mobx-react";
import { Box, Card, VBox } from "../components-v2/Box";
import { EmptyState } from "../components-v2/EmptyState";
import { Icon } from "../components-v2/Icon";
import { AppBarData } from "../appBar/appBarDomain";
import { DropdownMenu, DropdownMenuItemType } from "../components-v2/Dropdown";
import { isProductionEnvironment } from "../utils/environments";
import { classNames } from "../utils/classNames";
import { runInAction } from "mobx";
import { Button, LinkButton } from "../components-v2/Button";
import { Alert } from "../components-v2/Alert";
import { useLanguage, useRootStore, useTranslation } from "../stores/RootStoreContext";
import { Markdown } from "../text/Markdown";
import { navigateToUserSettings, openUserTermsInNewTab } from "../utils/navigation";
import { Checkbox } from "../components-v2/Checkbox";
import { StringFieldModel } from "../common/commonForms";
import DefaultLayout from "../containers/DefaultLayout";
import Form from "../components-v2/Form";

type NewPortfolioDialogProps = {
  apiService: ApiService;
  onSuccess?: (portfolio: Portfolio) => void;
  onClose?: () => void;
};

export const NewPortfolioDialog = observer((props: NewPortfolioDialogProps) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<Result<TMessage, TMessage> | undefined>(undefined);
  const model = useMemo(() => new StringFieldModel({ required: true, validateEmpty: true }), []);
  const submit = () => {
    model.commit();
    if (model.validate()) {
      setResult(pending());
      props.apiService
        .createPortfolio(model.validatedValue)
        .then((created) => runInAction(() => props.onSuccess?.(created)))
        .catch(() => {
          runInAction(() => setResult(failure({ key: "general-error" })));
        });
    }
  };
  return (
    <Dialog
      title={t("portfolio.create")}
      onCancel={props.onClose}
      onSubmit={submit}
      submitResult={result}
    >
      <VBox gap={"md"}>
        <Alert
          type={"info"}
          message={<Markdown value={t("open.beta.access.info")} />}
          icon={"info"}
        />
        <MTextInput model={model} label={t("name")} disabled={isOk(result)} />
        <div className={"subtle text-sm"}>{t("portfolio.about.ownership")}</div>
      </VBox>
    </Dialog>
  );
});

export const ConfirmDissolveDialog = observer(
  ({
    state,
    actions,
    contactsAdminsUrl,
  }: {
    state: PortfolioSettingsState;
    actions: PortfolioSettingsActions;
    contactsAdminsUrl?: string;
  }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        title={t("portfolio.dissolve-portfolio.title")}
        onCancel={actions.closeConfirmDissolvePortfolio}
        onSubmit={actions.dissolvePortfolio}
        submitResult={state.portfolioSettingsUpdateResult}
        submitLabel={t("confirm")}
      >
        <VBox gap={"md"}>
          <Alert
            type={"warning"}
            icon={"warning"}
            message={
              <Markdown
                value={t("portfolio.portfolio-settings.warning-message", {
                  portfolioName: state.portfolioSettingsModel.name.inputValue,
                  url: contactsAdminsUrl,
                })}
                inheritLinkColor
              />
            }
          />
          <p>{t("portfolio.dissolve-portfolio.description")}</p>
          <p>{t("sure-you-want-to-proceed")}</p>
          {!isProductionEnvironment() && (
            <VBox>
              <b>Demo only</b>
              <Checkbox
                label={t("portfolio.offboard-companies")}
                checked={state.offboardCompanies === true}
                onChange={actions.toggleOffboardCompanies}
              />
            </VBox>
          )}
        </VBox>
      </Dialog>
    );
  },
);

export const PortfolioSettingsDialog = observer(
  ({
    state,
    actions,
    contactsAdminsUrl,
  }: {
    state: PortfolioSettingsState;
    actions: PortfolioSettingsActions;
    contactsAdminsUrl?: string;
  }) => {
    const { t } = useTranslation();
    const formDisabled =
      isPending(state.portfolioSettingsUpdateResult) || isOk(state.portfolioSettingsUpdateResult);
    return (
      <Dialog
        title={t("portfolio.portfolio-settings")}
        onCancel={actions.closePortfolioSettings}
        actions={
          <Box justifyContent="space-between">
            <LinkButton
              color={"error"}
              label={t("portfolio.dissolve-portfolio")}
              icon="trash"
              onClick={actions.confirmDissolvePortfolio}
            />
            <Box>
              <Button
                label={t("cancel")}
                variant="secondary"
                onClick={actions.closePortfolioSettings}
                disabled={formDisabled}
              />
              <Button
                variant={"primary"}
                disabled={formDisabled}
                label={t("save")}
                loading={isPending(state.portfolioSettingsUpdateResult)}
                onClick={actions.savePortfolioSettings}
              />
            </Box>
          </Box>
        }
      >
        <VBox gap={"md"}>
          {state.portfolioCreationInfo && <div>{t(state.portfolioCreationInfo)}</div>}
          <MTextInput
            model={state.portfolioSettingsModel.name}
            label={t("title") + "*"}
            disabled={formDisabled}
          />
          <Alert
            type={"warning"}
            icon={"warning"}
            message={
              <Markdown
                value={t("portfolio.portfolio-settings.warning-message", {
                  portfolioName: state.portfolioSettingsModel.name.inputValue,
                  url: contactsAdminsUrl,
                })}
                inheritLinkColor
              />
            }
          />
        </VBox>
      </Dialog>
    );
  },
);

export const InvalidPortfolioView = observer(() => {
  const { t } = useTranslation();
  return (
    <Box grow={"golden"} alignItems={"center"}>
      <EmptyState
        illustration={<Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />}
        title={t("portfolio.invalidIdTitle")}
        description={t("portfolio.invalidIdText")}
      />
    </Box>
  );
});

export const PortfolioSelector = observer(function PortfolioSelector({
  selectedPortfolioOption,
  actions,
  maximizeDropdown,
  buttonClassName,
  user,
  portfolios,
}: AppBarData & {
  maximizeDropdown?: boolean;
  buttonClassName?: string;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      maximize={maximizeDropdown}
      disableOffset={maximizeDropdown}
      button={({ ref, isOpen, ...props }) => (
        <PortfolioSelectorButton
          ref={(el) => {
            if (el && maximizeDropdown) {
              ref(el.closest("header"));
            } else {
              ref(el);
            }
          }}
          className={buttonClassName}
          isOpen={isOpen}
          selectedPortfolioOption={selectedPortfolioOption || portfolioAllCompanies}
          userName={user.name}
          {...props}
        />
      )}
      items={[
        ...[
          portfolioAllCompanies,
          portfolioWithAdminRole,
          portfolioWithBoardRole,
          portfolioMyInvestments,
        ].map<DropdownMenuItemType>((portfolioSelectionOption) => ({
          type: "item",
          label: t(portfolioSelectionOption.label),
          icon: portfolioSelectionOption.icon,
          onSelect: () => actions.filterCompaniesInPortfolio?.(portfolioSelectionOption),
        })),
        { type: "separator" },
        { type: "label", label: t("portfolios") },
        ...(portfolios ?? []).map<DropdownMenuItemType>(({ name, id, createdAt, createdBy }) => ({
          type: "item",
          label: name,
          icon: "suitcaseSimple",
          onSelect: () => actions.openPortfolio?.({ id, name, createdAt, createdBy }),
        })),
        actions.openCreatePortfolioDialog && {
          type: "item",
          label: t("portfolio.new"),
          icon: "plus",
          onSelect: actions.openCreatePortfolioDialog,
        },
      ]}
    />
  );
});
const PortfolioSelectorButton = forwardRef<
  HTMLButtonElement,
  {
    selectedPortfolioOption: Required<AppBarData>["selectedPortfolioOption"];
    isOpen: boolean;
    userName: string;
  } & ButtonHTMLAttributes<HTMLButtonElement>
>(function PortfolioSelectorButton(
  { selectedPortfolioOption, isOpen, userName, className, ...props },
  ref,
) {
  const { t } = useTranslation();

  return (
    <button
      {...props}
      ref={ref}
      className={classNames(
        className,
        "tw-outline-offset-[-4px]",
        "tw-flex",
        "tw-items-center",
        "tw-gap-2",
        "tw-min-w-0",
      )}
    >
      <div className={"tw-min-w-0"}>
        <div
          className={
            "tw-font-semibold tw-overflow-hidden tw-overflow-ellipsis tw-whitespace-nowrap"
          }
        >
          {t(selectedPortfolioOption.label)}
        </div>
        <div className={"text-sm tw-overflow-hidden tw-overflow-ellipsis tw-whitespace-nowrap"}>
          {userName}
        </div>
      </div>
      <Icon name={isOpen ? "caret-up" : "caret-down"} />
    </button>
  );
});

export const IsEmailVerifiedAlert = observer(
  ({ isEmailVerified }: { isEmailVerified: boolean }) => {
    const history = useRootStore().history;
    const { t } = useTranslation();
    return !isEmailVerified ? (
      <Alert
        icon={"warning"}
        message={
          <Box>
            {t("portfolio.email-not-verified.text")}
            <LinkButton
              label={t("portfolio.email-not-verified.link")}
              onClick={() => navigateToUserSettings(history)}
              color={"inherit"}
            />
          </Box>
        }
        type={"warning"}
        paddingX={"md"}
        paddingY={"sm"}
      />
    ) : null;
  },
);

export const UserAcceptTerms: FC<{
  state: AcceptedTermsPreparedState;
}> = observer(({ state }) => {
  const { t } = useTranslation();
  const locale = useLanguage();
  const companyNamesText =
    state.numberOfCompanies === 0
      ? t("registerUser.infoNoCompanies")
      : t("registerUser.infoHasCompanies", { companies: state.commaSeparatedCompanies });

  return (
    <>
      <DefaultLayout withHeader={false} maxWidth={"sm"} fullheight>
        <VBox justifyContent={"center"} fullheight>
          <h1>{t("registerUser.welcome", { name: state.fullName })}</h1>
          <Card gap={"md"}>
            <p>{companyNamesText}</p>
            <p>{t("registerUser.whyEmail")}</p>
            <Form submitOnEnter onSubmit={state.acceptUserTermsAndUpdateEmailAddress}>
              <MTextInput
                label={t("registerUser.emailCaption")}
                model={state.emailModel}
                autofocus
              />
            </Form>
            <Checkbox
              id={"userTerms"}
              checked={state.acceptedTermsModel.value ?? false}
              onChange={state.acceptedTermsModel.toggle}
              label={
                <>
                  {t("registerUser.agreeToTerms")}
                  <span> </span>
                  <LinkButton
                    label={t("registerUser.agreeToTermsLink")}
                    onClick={() => {
                      openUserTermsInNewTab(locale);
                    }}
                  />
                </>
              }
            />
            {!state.acceptedTermsModel.isValid && (
              <Box fg={"error"}>{t("registerUser.agreeToTerms.validationError")}</Box>
            )}
            <div>
              <Button
                variant={"primary"}
                label={t("registerUser.completeUserRegistration")}
                loading={state.isAcceptingTerms}
                onClick={state.acceptUserTermsAndUpdateEmailAddress}
              />
            </div>
          </Card>
        </VBox>
      </DefaultLayout>
    </>
  );
});
